import { useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { getPairings, LEVEL_META, type Flavor } from '../data'
import { getLevelColors } from '../data/flavorMeta'
import FlavorWheel from './FlavorWheel'

interface FlavorDetailProps {
  flavor: Flavor
  onBack: () => void
}

type ViewMode = 'wheel' | 'list'

/** Пояснение уровня — одна строка под парой. */
const LEVEL_HINTS: Record<string, string> = {
  classic: 'Проверенное веками сочетание — беспроигрышная основа десерта',
  interesting: 'Небанальная пара, которую одобряет автор книги',
  bold: 'Смелый эксперимент — для тех, кто готов удивлять',
}

/**
 * Экран одного вкуса: колесо сочетаний (по умолчанию) или список.
 * Пара — конечная точка: показываем сочетание и его уровень смелости,
 * дальше ничего не открывается.
 */
const FlavorDetail = ({ flavor, onBack }: FlavorDetailProps) => {
  const pairings = useMemo(() => getPairings(flavor), [flavor])
  const [view, setView] = useState<ViewMode>('wheel')
  const [activeIndex, setActiveIndex] = useState(0)

  const active = pairings[activeIndex] ?? pairings[0]
  const activeColors = getLevelColors(active?.level ?? null)
  const activeMeta = active?.level ? LEVEL_META[active.level] : null

  return (
    <div className="flavor-detail">
      <div className="flavor-detail__topbar">
        <button type="button" className="flavors-back" onClick={onBack}>
          <Icon icon="solar:arrow-left-linear" width={20} height={20} />
          Все вкусы
        </button>

        <div className="flavors-view-toggle">
          <button
            type="button"
            className={`flavors-view-toggle__btn ${view === 'wheel' ? 'active' : ''}`}
            onClick={() => setView('wheel')}
            aria-label="Колесо"
          >
            <Icon icon="solar:pie-chart-2-linear" width={18} height={18} />
          </button>
          <button
            type="button"
            className={`flavors-view-toggle__btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
            aria-label="Список"
          >
            <Icon icon="solar:list-linear" width={18} height={18} />
          </button>
        </div>
      </div>

      <h1 className="flavor-detail__title">{flavor.name}</h1>
      <p className="flavor-detail__subtitle">
        {pairings.length} сочетаний из «Тезауруса вкусов»
      </p>

      <div className="flavors-legend">
        {(Object.keys(LEVEL_META) as (keyof typeof LEVEL_META)[]).map(level => (
          <span key={level} className="flavors-legend__item">
            <span
              className="flavors-legend__dot"
              style={{ background: getLevelColors(level).main }}
            />
            {LEVEL_META[level].label}
          </span>
        ))}
      </div>

      {view === 'wheel' ? (
        <>
          <FlavorWheel
            key={flavor.slug}
            pairings={pairings}
            centerName={flavor.name}
            centerSub={`${pairings.length} пар`}
            onActiveChange={setActiveIndex}
          />

          {active && (
            <div className="wheel-pair" aria-live="polite">
              <p className="wheel-pair__names">
                {flavor.name}
                <span className="wheel-pair__x"> × </span>
                {active.name}
              </p>
              <p
                className="wheel-pair__level"
                style={{ color: activeColors.text }}
              >
                {activeMeta ? activeMeta.label : 'Сочетание из книги'}
              </p>
              <p className="wheel-pair__hint">
                {active.level ? LEVEL_HINTS[active.level] : ''}
              </p>
            </div>
          )}
        </>
      ) : (
        <ul className="pairings-list">
          {pairings.map(pair => {
            const colors = getLevelColors(pair.level)
            const meta = pair.level ? LEVEL_META[pair.level] : null
            return (
              <li key={pair.slug} className="pairing-row">
                <span
                  className="pairing-row__dot"
                  style={{ background: colors.main }}
                  aria-hidden="true"
                />
                <span className="pairing-row__name">{pair.name}</span>
                {meta && (
                  <span
                    className="pairing-row__level"
                    style={{ color: colors.text }}
                  >
                    {meta.label}
                  </span>
                )}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default FlavorDetail
